/**
 * Audio Conversion Service
 * Handles WAV compression and conversion utilities
 */

import { CompressionSettings, getCompressionSettings, mp3QualityToCompressionLevel, Mp3QualityLevel } from './audioCompressionUtils';

/**
 * Simulate MP3 encoding with highly compressed WAV
 */
export const audioBufferToMp3 = async (
  buffer: AudioBuffer, 
  bitRate: Mp3QualityLevel = 128
): Promise<Blob> => {
  console.log('🎵 MP3 encoding not available, using highly compressed WAV...');
  
  const compressionLevel = mp3QualityToCompressionLevel(bitRate);
  return audioBufferToWav(buffer, true, compressionLevel);
};

/**
 * Enhanced WAV export with multiple compression levels
 */
export const audioBufferToWav = (
  buffer: AudioBuffer, 
  compress: boolean = false, 
  compressionLevel: number = 1
): Blob => {
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  
  let settings: CompressionSettings;
  
  if (compress) {
    settings = getCompressionSettings(compressionLevel as any, sampleRate);
  } else {
    settings = {
      bitDepth: 16,
      sampleRate: sampleRate,
      compressionLevel: 0
    };
  }
  
  // Calculate downsample ratio
  const downsampleRatio = sampleRate / settings.sampleRate;
  const downsampledLength = Math.floor(buffer.length / downsampleRatio);
  
  const bytesPerSample = Math.ceil(settings.bitDepth / 8);
  const blockAlign = numberOfChannels * bytesPerSample;
  const totalSamples = downsampledLength * numberOfChannels;
  
  console.log(`🔊 Converting AudioBuffer to ${compress ? 'compressed ' : ''}WAV format...`, {
    bufferLength: buffer.length,
    numberOfChannels,
    bitDepth: settings.bitDepth,
    originalSampleRate: sampleRate,
    downsampleRate: settings.sampleRate,
    compressionLevel: settings.compressionLevel,
    downsampledLength,
    totalSamples,
    estimatedSizeMB: (44 + totalSamples * bytesPerSample) / (1024 * 1024)
  });  
  // Calculate total size and create ArrayBuffer directly
  const dataByteLength = totalSamples * bytesPerSample;
  const arrayBuffer = new ArrayBuffer(44 + dataByteLength);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataByteLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, settings.sampleRate, true); // Use downsampled rate
  view.setUint32(28, settings.sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, settings.bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataByteLength, true);
  
  // Write PCM data with advanced compression
  let offset = 44;
  const maxValue = Math.pow(2, settings.bitDepth - 1) - 1;
  const minValue = -Math.pow(2, settings.bitDepth - 1);
  
  for (let i = 0; i < downsampledLength; i++) {
    // Calculate source index with downsampling
    const sourceIndex = Math.floor(i * downsampleRatio);
    
    for (let channel = 0; channel < numberOfChannels; channel++) {
      if (sourceIndex < buffer.length) {
        let sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[sourceIndex]));
        
        // Apply dynamic range compression for better quality at lower bit depths
        if (compress && settings.compressionLevel >= 3) {
          // Soft compression curve
          const threshold = 0.7;
          if (Math.abs(sample) > threshold) {
            const excess = Math.abs(sample) - threshold;
            const compressedExcess = excess * 0.3; // 3:1 compression ratio
            sample = Math.sign(sample) * (threshold + compressedExcess);
          }
        }
        
        const intSample = Math.round(sample * maxValue);
        const clampedSample = Math.max(minValue, Math.min(maxValue, intSample));
        
        // Write based on bit depth
        if (settings.bitDepth === 8) {
          view.setUint8(offset, clampedSample + 128); // 8-bit is unsigned
          offset += 1;
        } else if (settings.bitDepth <= 16) {
          view.setInt16(offset, clampedSample, true);
          offset += 2;
        } else {
          view.setInt32(offset, clampedSample, true);
          offset += 4;
        }
      } else {
        // Silence for out-of-bounds
        if (settings.bitDepth === 8) {
          view.setUint8(offset, 128);
          offset += 1;
        } else {
          view.setInt16(offset, 0, true);
          offset += 2;
        }
      }
    }
    
    // Progress logging for very large files
    if (i > 0 && i % (settings.sampleRate * 10) === 0) {
      console.log(`🔊 WAV conversion progress: ${((i / downsampledLength) * 100).toFixed(1)}%`);
    }
  }
  
  console.log('🔊 WAV conversion complete. Total size:', (arrayBuffer.byteLength / (1024 * 1024)).toFixed(2), 'MB');
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};
